import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { cn } from "@/utils/theme";
import { useMemo } from "react";
import type { ClassNameValue } from "tailwind-merge";

interface Props {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onNext: () => void;
  onPrevious: () => void;
  hasNext: boolean;
  hasPrevious: boolean;
  classNameItem?: ClassNameValue;
}

type PaginationValue = number | "ELLIPSIS";

type PaginationEntry = {
  key: string;
  value: PaginationValue;
};

const TablePaginantion = ({
  hasNext,
  hasPrevious,
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  classNameItem,
  onNext,
  onPageChange,
  onPrevious,
}: Props) => {
  const paginationItems: PaginationEntry[] = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1).map(
        (page) => ({ key: `page-${page}`, value: page }),
      );
    }

    const items = new Set<number>();
    items.add(1);
    items.add(totalPages);

    for (let i = currentPage - 1; i <= currentPage + 1; i++) {
      if (i > 1 && i < totalPages) items.add(i);
    }

    const sorted = Array.from(items).sort((a, b) => a - b);
    const result: PaginationEntry[] = [];
    let prev = 0;

    for (const page of sorted) {
      if (prev > 0) {
        if (page - prev === 2) {
          result.push({ key: `page-${prev + 1}`, value: prev + 1 });
        } else if (page - prev > 2) {
          result.push({ key: `ellipsis-${prev}-${page}`, value: "ELLIPSIS" });
        }
      }
      result.push({ key: `page-${page}`, value: page });
      prev = page;
    }
    return result;
  }, [currentPage, totalPages]);

  return (
    <div className="w-full flex items-center justify-between font-geist">
      <p className="text-sm text-body leading-2xl">
        Mostrando{" "}
        <span className="font-semibold text-body-loud">
          {currentPage === totalPages
            ? totalItems - (currentPage - 1) * pageSize
            : pageSize}
        </span>{" "}
        de <span className="font-semibold text-body-loud">{totalItems}</span>{" "}
        resultados.
      </p>
      <Pagination>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={onPrevious}
              disabled={!hasPrevious}
              className="cursor-pointer"
            />
          </PaginationItem>

          {paginationItems.map(({ key, value: item }) => {
            if (item === "ELLIPSIS") {
              return (
                <PaginationItem key={key}>
                  <PaginationLink
                    className="size-8 cursor-pointer"
                    variant="quiet"
                    group="middle"
                  >
                    <PaginationEllipsis />
                  </PaginationLink>
                </PaginationItem>
              );
            }

            const page = item;
            return (
              <PaginationItem key={key} className="hidden tablet:block">
                <PaginationLink
                  className={cn(
                    "size-8 cursor-pointer",
                    classNameItem,
                    page === 1 && currentPage === page && "border-l",
                  )}
                  variant="quiet"
                  group="middle"
                  isActive={currentPage === page}
                  onClick={() => {
                    onPageChange(page);
                  }}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            );
          })}

          <PaginationItem>
            <PaginationNext
              className="cursor-pointer"
              onClick={onNext}
              disabled={!hasNext}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};

export default TablePaginantion;
